"use client";

import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Icon, type IconName } from "../Icon";

export interface SearchResultItemProps {
  /**
   * Unique identifier for the result
   */
  id: string;
  /**
   * Primary title text
   */
  title: string;
  /**
   * Optional subtitle or description
   */
  subtitle?: string;
  /**
   * Optional metadata text (e.g., "Last updated 2 days ago")
   */
  metadata?: string;
  /**
   * Icon to display
   */
  icon?: IconName;
  /**
   * Custom icon component (overrides icon prop)
   */
  customIcon?: ReactNode;
  /**
   * Handler for when the result is clicked
   */
  onSelect?: (id: string) => void;
  /**
   * Whether this result is currently focused/highlighted
   */
  isFocused?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Test ID for testing
   */
  testId?: string;
}

/**
 * SearchResultItem
 *
 * Individual search result item with icon, title, subtitle, and metadata.
 * Supports keyboard navigation and hover states.
 *
 * @example
 * ```tsx
 * <SearchResultItem
 *   id="site-123"
 *   title="Headquarters"
 *   subtitle="San Francisco, CA"
 *   metadata="Site"
 *   icon="MapPin"
 *   onSelect={(id) => navigate(`/sites/${id}`)}
 * />
 * ```
 */
export function SearchResultItem({
  id,
  title,
  subtitle,
  metadata,
  icon,
  customIcon,
  onSelect,
  isFocused = false,
  className,
  testId,
}: SearchResultItemProps) {
  const handleClick = () => {
    onSelect?.(id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const target = e.currentTarget;

    switch (e.key) {
      case "Enter":
      case " ": {
        e.preventDefault();
        onSelect?.(id);
        break;
      }
      case "ArrowDown": {
        e.preventDefault();
        // Find all search results in the document and navigate to the next one
        const allResults = Array.from(document.querySelectorAll<HTMLElement>("[data-search-result]"));
        const currentIndex = allResults.indexOf(target as HTMLElement);
        if (currentIndex !== -1 && currentIndex < allResults.length - 1) {
          allResults[currentIndex + 1].focus();
        }
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        // Find all search results and navigate to the previous one
        const allResults = Array.from(document.querySelectorAll<HTMLElement>("[data-search-result]"));
        const currentIndex = allResults.indexOf(target as HTMLElement);
        if (currentIndex > 0) {
          allResults[currentIndex - 1].focus();
        } else {
          // Go back to search input
          const input = document.querySelector<HTMLInputElement>('input[type="text"][placeholder*="Search"]');
          if (input) {
            input.focus();
          }
        }
        break;
      }
      case "Escape": {
        e.preventDefault();
        // Close the popover by focusing the input
        const input = document.querySelector<HTMLInputElement>('input[type="text"][placeholder*="Search"]');
        if (input) {
          input.focus();
        }
        break;
      }
    }
  };

  return (
    <div
      role="option"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid={testId}
      data-search-result
      className={twMerge(
        "group flex items-center gap-3 px-4 py-3 cursor-pointer outline-none transition-colors",
        "text-text-body hover:bg-background-hover",
        "focus-visible:bg-brand-light focus-visible:outline-none",
        "data-[focused]:bg-background-hover",
        isFocused && "bg-background-hover",
        className
      )}
      data-focused={isFocused || undefined}
    >
      {/* Icon */}
      {customIcon ? (
        <div className="flex-shrink-0">{customIcon}</div>
      ) : icon ? (
        <div className="flex-shrink-0 text-text-muted">
          <Icon name={icon} size={20} />
        </div>
      ) : null}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-text-heading truncate">{title}</span>
          {metadata && <span className="text-xs text-text-muted flex-shrink-0">{metadata}</span>}
        </div>
        {subtitle && <div className="text-sm text-text-muted truncate mt-0.5">{subtitle}</div>}
      </div>

      {/* Arrow indicator on hover/focus */}
      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 group-data-[focused]:opacity-100 transition-opacity">
        <Icon name="ArrowRight" size={16} className="text-text-muted" />
      </div>
    </div>
  );
}
